import {styled} from "@mui/material/styles";
import {
	Drawer,
  Icon,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip as MuiTooltip,
  Paper,
} from "@mui/material";
import { MediaQuerys } from "../../styles/GlobalStyle";
import { useColors } from "../../Context/ColorsContext"

const drawerWidth = 250;
const { mobile, tablet, desktop } = MediaQuerys;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  [desktop]:{
    width: '200px'
  }
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

export const DrawerHeader = styled("div")(({ theme }) => ({
  color: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

export const MiniDrawer = styled(Drawer)(({ theme }) => ({
    width: `calc(${theme.spacing(9)} + 1px)`,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    zIndex: 999,
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
}));

export const CustomDrawer = styled(Drawer)(({ theme }) => ({
    width: drawerWidth,
    flexShrink: 0,
    zIndex: 1200,
    '& .MuiDrawer-paper': {
      width: drawerWidth,
      boxSizing: 'border-box',
      boxShadow: 'rgb(61 71 82 / 20%) 0px 4px 8px',
    },
}));

export const PopperContainer = styled(Paper)(({ theme, ownerState }) => {
  const { colors } = useColors();

  return {
    width: 250,
    py: 1,
    background: colors.sidebarSelectColor,
    color: colors.navbarColor,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 0,
    boxShadow: 'none',
    '& .MuiTypography-root': {
        color: colors.navbarColor,
        fontSize: '14px',
        [theme.breakpoints.down('md')]: {
           fontSize: '12px',
        }
    },
    minHeight: ownerState?.minHeight || 'auto',
    overflowY: 'auto',
    overflowX: 'hidden',

    // Firefox
    scrollbarWidth: 'thin',
    scrollbarColor: '#c1c1c1 #f5f5f5',

    // WebKit (Chrome, Safari, Edge)
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#f5f5f5',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#c1c1c1',
      borderRadius: '10px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: '#a8a8a8',
    },

    animation: 'popperFadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '@keyframes popperFadeIn': {
        '0%': { opacity: 0, transform: 'translateY(-10px)' },
        '100%': { opacity: 1, transform: 'translateY(0)' }
    },
  };
});

export const MiniItemWrapper = styled('div')(() => {
  const { colors } = useColors();
  return {
    '&:hover .MuiListItemButton-root': {
      background: colors.sidebarSelectColor,
    },
    '&:hover .MuiListItemButton-root > *': {
      color: colors.white,
    },
  };
});


export const Item = styled(ListItemButton)(() => {
    const { colors } = useColors();

    return {
        '&.Mui-selected > *': {
            color: colors.navbarColor,
        },
        '&.Mui-selected': {
            background: '#f4f4f4',
            position: 'relative',
            '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: '6px',
                backgroundColor: colors.sidebarSelectColor,
            },
            ':hover': {
                background: colors.sidebarSelectColor,
            },
            ':hover > *': {
                color: colors.navbarColor,
            }
        },
        ':hover > *': {
            color: colors.navbarColor,
        },
        ':hover': {
            background: colors.sidebarSelectColor,
        },
        color: colors.grayFont, 
        width: '100%',
        paddingLeft: '10px'
    }
})

export const SidebarItem = styled(ListItemButton)(() => {
    const { colors } = useColors();

    return {
        '&.Mui-selected > *': {
            color: colors.sidebarSelectColor,
        },
        '&.Mui-selected': {
            background: '#f4f4f4',
            position: 'relative',
            '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: '6px',
                backgroundColor: colors.sidebarSelectColor,
            },
            ':hover': {
                background: colors.sidebarSelectColor,
            },
            ':hover > *': {
                color: colors.navbarColor,
            }
        },
        ':hover > *': {
            color: colors.navbarColor,
        },
        ':hover': {
            background: colors.sidebarSelectColor,
        },
        color: colors.grayFont, 
        width: '100%',
    }
})

export const ItemIcon = styled(ListItemIcon)(({ theme }) => {
    return  {
        padding: '10px 1px',
        minWidth: '30px',
        color: '#b8b9baff',
        [desktop]: {
          ".MuiListItemIcon-root": {
              fontSize: "0.20rem !important",
            },
          ".material-icons-outlined": {
            fontSize: '20px !important',
          },
        },
        [theme.breakpoints.up('xl')]: {
          padding: '10px 1px',
        },
        [theme.breakpoints.down('xl')]: {
          padding: '6px 1px',
        },
    }
})

export const ItemText = styled(ListItemText)(({ theme }) => {
  const { colors } = useColors();

  return {
    '.MuiTypography-root': {
      fontSize: "14px !important",
      color: `${colors.navbarColor} !important`,
      [theme.breakpoints.down('md')]: {
        fontSize: "12px !important",
      }
    },
  };
});

export const CustomIcon = styled(Icon)(() => ({
  fontSize: 24,
}));

export const PopperHeader = styled(ListItemButton)(({ theme }) => {
  const { colors } = useColors();
  return {
		position: 'sticky',
		top: 0,
		zIndex: 10,
		backgroundColor: colors.sidebarSelectColor,
		opacity: '1 !important', // Forzar opacidad total
		'&:hover': {
			backgroundColor: colors.sidebarSelectColor,
		},
		[theme.breakpoints.up('xl')]: {
			height: '60px',
		},
		[theme.breakpoints.down('xl')]: {
			height: '52px',
		},
		'&::before': {
			display: 'none',
		}
	}
});